import { useEffect, useMemo, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  CircularProgress,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from '../contexts/SnackbarContext';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

export default function Payroll() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = user?.role === 'ADMIN';
  const [tab, setTab] = useState(0);
  const [month, setMonth] = useState(getCurrentMonth());
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [ruleDrafts, setRuleDrafts] = useState([]);
  const isAllUsers = isAdmin && selectedUserId === 'all';

  const fetchSummary = async (userId) => {
    try {
      const res = await API.get('/payroll/summary', {
        params: { month, user_id: userId || undefined },
      });
      setSummary(res.data);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement', 'error');
    }
  };

  const fetchSales = async (userId) => {
    try {
      const params = { month };
      if (selectedDate) params.date = selectedDate;
      let endpoint = '/logs/me';
      if (isAdmin && userId === 'all') {
        endpoint = '/logs';
      } else if (isAdmin && userId) {
        endpoint = `/logs/seller/${userId}`;
      }
      const res = await API.get(endpoint, { params });
      setSales(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement des ventes', 'error');
    }
  };

  const fetchUsersAndRules = async () => {
    if (!isAdmin) return;
    try {
      const [usersRes, rulesRes] = await Promise.all([
        API.get('/users'),
        API.get('/payroll/rules'),
      ]);
      const usersData = usersRes.data || [];
      const rulesData = rulesRes.data || [];
      setUsers(usersData);
      setRules(rulesData);
      if (usersData.length > 0) {
        setSelectedUserId((prev) => prev || 'all');
      }

      const merged = usersData.map((u) => {
        const rule = rulesData.find((r) => r.user_id === u.id);
        return {
          user_id: u.id,
          username: u.username,
          role: u.role,
          base_salary: rule?.base_salary ?? 0,
          commission_type: rule?.commission_type ?? 'REVENUE',
          commission_rate: rule?.commission_rate ?? 0,
          target_value: rule?.target_value ?? 0,
          active: rule?.active !== 0,
        };
      });
      setRuleDrafts(merged);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement', 'error');
    }
  };

  const fetchHistory = async (userId) => {
    try {
      const res = await API.get('/payroll/history', {
        params: { user_id: userId || undefined, limit: 12 },
      });
      setHistory(res.data?.series || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement de l\'historique', 'error');
    }
  };

  useEffect(() => {
    setLoading(true);
    const userId = isAdmin ? selectedUserId : undefined;
    Promise.all([fetchSummary(userId), fetchSales(userId), fetchHistory(userId)]).finally(() => setLoading(false));
  }, [month, selectedUserId, selectedDate, isAdmin]);

  useEffect(() => {
    fetchUsersAndRules();
  }, [isAdmin]);

  const handleRuleChange = (index, field, value) => {
    setRuleDrafts((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const saveRule = async (rule) => {
    try {
      await API.post('/payroll/rules', {
        user_id: rule.user_id,
        base_salary: Number(rule.base_salary),
        commission_type: rule.commission_type,
        commission_rate: Number(rule.commission_rate),
        target_value: Number(rule.target_value),
        active: !!rule.active,
      });
      showSnackbar('Regle enregistree', 'success');
      fetchUsersAndRules();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const achievedPercent = useMemo(() => {
    const target = summary?.targetSalary || 0;
    const achieved = summary?.achievedSalary || 0;
    if (!target) return 0;
    return Math.min(100, Math.round((achieved / target) * 100));
  }, [summary]);


  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          Mes ventes & salaire
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 520 }}>
          Suivi des ventes et de la commission.
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          background: 'var(--surface-1)',
          border: '1px solid var(--border-subtle)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="month"
            label="Mois"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 180 }}
          />
          <TextField
            type="date"
            label="Jour"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 160 }}
          />
          {isAdmin && (
            <TextField
              select
              label="Employe"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 200 }}
              size="small"
            >
              <MenuItem value="all">Tous</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.username}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isAdmin && (
            <Tabs
              value={tab}
              onChange={(e, newValue) => setTab(newValue)}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                flex: 1,
                '& .MuiTab-root': { color: 'var(--text-muted)', fontWeight: 700 },
                '& .Mui-selected': { color: 'var(--text-primary)' },
              }}
            >
              <Tab label="Vue employe" />
              <Tab label="Historique paiement" />
              <Tab label="Parametres" />
            </Tabs>
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Stack alignItems="center">
          <CircularProgress sx={{ color: 'var(--text-primary)' }} />
        </Stack>
      ) : (
        <>
          {(!isAdmin || tab === 0) && (
            <>
              {isMobile ? (
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    mb: 3,
                    background: 'var(--surface-1)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                      Resume du mois
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction="column" spacing={2}>
                      {[
                        { label: isAllUsers ? 'Total fixes' : 'Salaire fixe', value: formatCurrency(summary?.base_salary) },
                        { label: isAllUsers ? 'Total commissions' : 'Commission', value: formatCurrency(summary?.commission) },
                        { label: isAllUsers ? 'Total salaires' : 'Salaire atteint', value: formatCurrency(summary?.achievedSalary) },
                        { label: isAllUsers ? 'Total objectifs' : 'Objectif', value: formatCurrency(summary?.targetSalary) },
                      ].map((item) => (
                        <Paper
                          key={item.label}
                          sx={{
                            p: 2.5,
                            borderRadius: 4,
                            background: 'var(--surface-2)',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 20 }}>
                            {item.value}
                          </Typography>
                        </Paper>
                      ))}
                      <Paper
                        sx={{
                          p: 2.5,
                          borderRadius: 4,
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                          Progression objectif
                        </Typography>
                        <Box
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            background: 'var(--surface-3)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${achievedPercent}%`,
                              background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
                            }}
                          />
                        </Box>
                        <Typography sx={{ color: 'var(--text-muted)', mt: 1 }}>
                          {achievedPercent}% atteint
                        </Typography>
                      </Paper>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
                  {[
                    { label: isAllUsers ? 'Total fixes' : 'Salaire fixe', value: formatCurrency(summary?.base_salary) },
                    { label: isAllUsers ? 'Total commissions' : 'Commission', value: formatCurrency(summary?.commission) },
                    { label: isAllUsers ? 'Total salaires' : 'Salaire atteint', value: formatCurrency(summary?.achievedSalary) },
                    { label: isAllUsers ? 'Total objectifs' : 'Objectif', value: formatCurrency(summary?.targetSalary) },
                  ].map((item) => (
                    <Paper
                      key={item.label}
                      sx={{
                        flex: 1,
                        p: 3,
                        borderRadius: 4,
                        background:
                          'var(--surface-1)',
                        border: '1px solid var(--border-subtle)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 22 }}>
                        {item.value}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}


              {!isMobile && (
                <Paper
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    border: '1px solid var(--border-subtle)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                    Progression objectif
                  </Typography>
                  <Box
                    sx={{
                      height: 12,
                      borderRadius: 999,
                      background: 'var(--surface-3)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${achievedPercent}%`,
                        background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: 'var(--text-muted)', mt: 1 }}>
                    {achievedPercent}% atteint
                  </Typography>
                </Paper>
              )}

              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background:
                    'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                  Ventes du mois
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          ...(isAllUsers ? ['Employe'] : []),
                          'Reference',
                          'Quantite',
                          'Total',
                          'Date',
                        ].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              color: 'var(--text-primary)',
                              fontWeight: 700,
                              backgroundColor: 'var(--table-header-bg)',
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sales.map((row) => (
                        <TableRow key={row.id}>
                          {isAllUsers && (
                            <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {row.username || '-'}
                            </TableCell>
                          )}
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.reference}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.quantity}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {formatCurrency(row.total_price)}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.created_at}
                          </TableCell>
                        </TableRow>
                      ))}
                      {sales.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isAllUsers ? 5 : 4} sx={{ color: 'var(--text-muted)' }}>
                            Aucune vente pour ce mois.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

          {isAdmin && tab === 1 && (
            <Paper
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                Historique (12 derniers mois)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        ...(isAllUsers ? ['Employe'] : []),
                        'Mois',
                        'Umsatz',
                        'Quantite',
                        'Commission',
                        'Salaire atteint',
                      ].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((row) => (
                      <TableRow key={`${row.month}-${row.user_id || 'me'}`}>
                        {isAllUsers && (
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.username || '-'}
                          </TableCell>
                        )}
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.month}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.revenue)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.quantity}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.commission)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.achievedSalary)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAllUsers ? 6 : 5} sx={{ color: 'var(--text-muted)' }}>
                          Aucune donnee disponible.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {isAdmin && tab === 2 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                  Parametres de commission
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const res = await API.get('/payroll/export.csv', {
                        responseType: 'blob',
                        params: { month },
                      });
                      const blob = new Blob([res.data], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `payroll-${month}.csv`;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      showSnackbar(err.response?.data?.message || 'Erreur lors de l\'export', 'error');
                    }
                  }}
                  sx={{
                    borderRadius: 999,
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)',
                  }}
                >
                  Export CSV
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Employe', 'Fixe', 'Type', 'Taux', 'Objectif', 'Actif', 'Action'].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ruleDrafts.map((row, index) => (
                      <TableRow key={row.user_id}>
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.username}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.base_salary}
                            onChange={(e) => handleRuleChange(index, 'base_salary', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={row.commission_type}
                            onChange={(e) => handleRuleChange(index, 'commission_type', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 120 }}
                          >
                            <option value="REVENUE">Umsatz %</option>
                            <option value="UNIT">Stueck</option>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.commission_rate}
                            onChange={(e) => handleRuleChange(index, 'commission_rate', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.target_value}
                            onChange={(e) => handleRuleChange(index, 'target_value', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={row.active ? 'yes' : 'no'}
                            onChange={(e) => handleRuleChange(index, 'active', e.target.value === 'yes')}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 90 }}
                          >
                            <option value="yes">Oui</option>
                            <option value="no">Non</option>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveRule(row)}
                          >
                            Enregistrer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {ruleDrafts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ color: 'var(--text-muted)' }}>
                          Aucun employe trouve.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}





